import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { setCsrfToken, isUnauthenticated } from './lib/api';
import { meQuery } from './lib/queries';
import { NAV_ITEMS, navItem, navVisible, type NavPath } from './nav';
import { RouteError, NotFound } from './components/states';
import { AppShell } from './components/app-shell';

export interface RouterContext {
  queryClient: QueryClient;
}

/* ---------------------------------------------------------------- search helpers */

type Raw = Record<string, unknown>;
const str = (v: unknown): string | undefined => {
  if (typeof v === 'string') return v.trim() === '' ? undefined : v;
  if (typeof v === 'number') return String(v);
  return undefined;
};

/* ---------------------------------------------------------------- root */

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFound,
  errorComponent: RouteError,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (s: Raw): { redirect?: string } => ({ redirect: str(s.redirect) }),
  component: lazyRouteComponent(() => import('./pages/login'), 'LoginPage'),
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context, location }) => {
    try {
      const me = await context.queryClient.ensureQueryData(meQuery());
      setCsrfToken(me.csrf_token);
      return { me };
    } catch (err) {
      if (isUnauthenticated(err)) {
        throw redirect({ to: '/login', search: { redirect: location.href } });
      }
      throw err;
    }
  },
  component: AppShell,
});

/** 权限即界面：无权限直接跳到无权限页（接口仍做最终校验） */
function guard(path: NavPath) {
  return ({ context }: { context: { me: Parameters<typeof navVisible>[0] } }) => {
    if (!navVisible(context.me, navItem(path))) throw redirect({ to: '/forbidden' });
  };
}

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    if (!navVisible(context.me, navItem('/'))) {
      const first = NAV_ITEMS.find((i) => i.to !== '/' && navVisible(context.me, i));
      throw redirect({ to: first?.to ?? '/forbidden' });
    }
  },
  component: lazyRouteComponent(() => import('./pages/dashboard'), 'DashboardPage'),
});

const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/users',
  beforeLoad: guard('/users'),
  validateSearch: (s: Raw): { q?: string; status?: string } => ({ q: str(s.q), status: str(s.status) }),
  component: lazyRouteComponent(() => import('./pages/users'), 'UsersPage'),
});

export const USER_TABS = ['overview', 'grants', 'limits', 'keys', 'tasks', 'ledger', 'assets'] as const;
export type UserTab = (typeof USER_TABS)[number];

const userDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/users/$userId',
  beforeLoad: guard('/users'),
  validateSearch: (s: Raw): { tab?: UserTab } => {
    const tab = str(s.tab);
    return { tab: (USER_TABS as readonly string[]).includes(tab ?? '') ? (tab as UserTab) : undefined };
  },
  component: lazyRouteComponent(() => import('./pages/user-detail'), 'UserDetailPage'),
});

export const QUOTA_TABS = ['grant', 'deduct', 'ledger'] as const;
export type QuotaTab = (typeof QUOTA_TABS)[number];

const quotaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/quota',
  beforeLoad: guard('/quota'),
  validateSearch: (s: Raw): { tab?: QuotaTab; user_id?: string; reference_no?: string; type?: string } => {
    const tab = str(s.tab);
    return {
      tab: (QUOTA_TABS as readonly string[]).includes(tab ?? '') ? (tab as QuotaTab) : undefined,
      user_id: str(s.user_id),
      reference_no: str(s.reference_no),
      type: str(s.type),
    };
  },
  component: lazyRouteComponent(() => import('./pages/quota'), 'QuotaPage'),
});

export const REFERRAL_TABS = ['referrers', 'commissions', 'settlements'] as const;
export type ReferralTab = (typeof REFERRAL_TABS)[number];

const referralsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/referrals',
  beforeLoad: guard('/referrals'),
  validateSearch: (s: Raw): { tab?: ReferralTab; q?: string; pending?: string; status?: string; referrer?: string } => {
    const tab = str(s.tab);
    return {
      tab: (REFERRAL_TABS as readonly string[]).includes(tab ?? '') ? (tab as ReferralTab) : undefined,
      q: str(s.q),
      pending: str(s.pending),
      status: str(s.status),
      referrer: str(s.referrer),
    };
  },
  component: lazyRouteComponent(() => import('./pages/referrals'), 'ReferralsPage'),
});

export const RESELLER_TABS = ['resellers', 'commissions', 'settlements'] as const;
export type ResellerTab = (typeof RESELLER_TABS)[number];
export const RESELLER_DETAIL_TABS = ['customers', 'commissions', 'settlements', 'members'] as const;
export type ResellerDetailTab = (typeof RESELLER_DETAIL_TABS)[number];

const resellersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/resellers',
  beforeLoad: guard('/resellers'),
  validateSearch: (
    s: Raw,
  ): { tab?: ResellerTab; q?: string; status?: string; reseller?: string; detail?: ResellerDetailTab } => {
    const tab = str(s.tab);
    const detail = str(s.detail);
    return {
      tab: (RESELLER_TABS as readonly string[]).includes(tab ?? '') ? (tab as ResellerTab) : undefined,
      q: str(s.q),
      status: str(s.status),
      reseller: str(s.reseller),
      detail: (RESELLER_DETAIL_TABS as readonly string[]).includes(detail ?? '') ? (detail as ResellerDetailTab) : undefined,
    };
  },
  component: lazyRouteComponent(() => import('./pages/resellers'), 'ResellersPage'),
});

export const TOPUP_TABS = ['deposits', 'claims', 'rules', 'networks', 'settings'] as const;
export type TopupTab = (typeof TOPUP_TABS)[number];

const topupRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/topup',
  beforeLoad: guard('/topup'),
  validateSearch: (s: Raw): { tab?: TopupTab; status?: string; chain?: string; q?: string; deposit?: string } => {
    const tab = str(s.tab);
    return {
      tab: (TOPUP_TABS as readonly string[]).includes(tab ?? '') ? (tab as TopupTab) : undefined,
      status: str(s.status),
      chain: str(s.chain),
      q: str(s.q),
      deposit: str(s.deposit),
    };
  },
  component: lazyRouteComponent(() => import('./pages/topup'), 'TopupPage'),
});

const redeemRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/redeem',
  beforeLoad: guard('/redeem'),
  component: lazyRouteComponent(() => import('./pages/redeem'), 'RedeemPage'),
});

const modelsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/models',
  beforeLoad: guard('/models'),
  component: lazyRouteComponent(() => import('./pages/models'), 'ModelsPage'),
});

export const MODEL_TABS = ['overview', 'capabilities', 'pricing'] as const;
export type ModelTab = (typeof MODEL_TABS)[number];

const modelDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/models/$modelId',
  beforeLoad: guard('/models'),
  validateSearch: (s: Raw): { tab?: ModelTab } => {
    const tab = str(s.tab);
    return { tab: (MODEL_TABS as readonly string[]).includes(tab ?? '') ? (tab as ModelTab) : undefined };
  },
  component: lazyRouteComponent(() => import('./pages/model-detail'), 'ModelDetailPage'),
});

const channelsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/channels',
  beforeLoad: guard('/channels'),
  validateSearch: (s: Raw): { model?: string } => ({ model: str(s.model) }),
  component: lazyRouteComponent(() => import('./pages/channels'), 'ChannelsPage'),
});

const assetsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/assets',
  beforeLoad: guard('/assets'),
  validateSearch: (s: Raw): { q?: string; user_id?: string } => ({ q: str(s.q), user_id: str(s.user_id) }),
  component: lazyRouteComponent(() => import('./pages/assets'), 'AssetsPage'),
});

const assetDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/assets/$assetId',
  beforeLoad: guard('/assets'),
  component: lazyRouteComponent(() => import('./pages/asset-detail'), 'AssetDetailPage'),
});

const tasksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tasks',
  beforeLoad: guard('/tasks'),
  validateSearch: (s: Raw): { status?: string; failure_code?: string; user_id?: string; q?: string } => ({
    status: str(s.status),
    failure_code: str(s.failure_code),
    user_id: str(s.user_id),
    q: str(s.q),
  }),
  component: lazyRouteComponent(() => import('./pages/tasks'), 'TasksPage'),
});

const taskDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tasks/$taskId',
  beforeLoad: guard('/tasks'),
  component: lazyRouteComponent(() => import('./pages/task-detail'), 'TaskDetailPage'),
});

export type AlertStatusFilter = 'open' | 'acked' | 'resolved' | 'all';

const alertsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/alerts',
  beforeLoad: guard('/alerts'),
  validateSearch: (s: Raw): { status?: AlertStatusFilter } => {
    const v = str(s.status);
    return { status: v === 'acked' || v === 'resolved' || v === 'all' || v === 'open' ? v : undefined };
  },
  component: lazyRouteComponent(() => import('./pages/alerts'), 'AlertsPage'),
});

const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/audit',
  beforeLoad: guard('/audit'),
  validateSearch: (s: Raw): { action?: string } => ({ action: str(s.action) }),
  component: lazyRouteComponent(() => import('./pages/audit'), 'AuditPage'),
});

const adminsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admins',
  beforeLoad: guard('/admins'),
  component: lazyRouteComponent(() => import('./pages/admins'), 'AdminsPage'),
});

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  beforeLoad: guard('/settings'),
  component: lazyRouteComponent(() => import('./pages/settings'), 'SettingsPage'),
});

const systemRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/system',
  beforeLoad: guard('/system'),
  component: lazyRouteComponent(() => import('./pages/system'), 'SystemPage'),
});

const releaseRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/release',
  beforeLoad: guard('/release'),
  component: lazyRouteComponent(() => import('./pages/release'), 'ReleasePage'),
});

const forbiddenRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/forbidden',
  component: lazyRouteComponent(() => import('./components/states'), 'Forbidden'),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  appRoute.addChildren([
    dashboardRoute,
    usersRoute,
    userDetailRoute,
    quotaRoute,
    referralsRoute,
    resellersRoute,
    topupRoute,
    redeemRoute,
    modelsRoute,
    modelDetailRoute,
    channelsRoute,
    assetsRoute,
    assetDetailRoute,
    tasksRoute,
    taskDetailRoute,
    alertsRoute,
    auditRoute,
    adminsRoute,
    settingsRoute,
    systemRoute,
    releaseRoute,
    forbiddenRoute,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
    defaultNotFoundComponent: NotFound,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
